import React, { useRef, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { intlShape } from 'react-intl';
import PropTypes from 'prop-types';
import { List as IList } from 'immutable';
import Link from '@mui/material/Link';
import Button from '@mui/material/Button';

import {
    Grid, Box, Typography, useTheme, useMediaQuery
} from '@mui/material';
import Markdown from 'markdown-to-jsx';
import DOMPurify from 'dompurify';
import { getCategorySpecificFaceplates } from '../../utils/landingPageUtils';
import {
    getDigitalPrimaryFaceplate,
    getPhysicalPrimaryFaceplate, getFaceplateGroups
} from '../../landingPage/landingPageSelectors';
import {
    saveSelectedCard,
    saveSelectedCardOnBuyButtonClick, selectedCardType, setClickedOnUYO
} from '../../landingPage/landingPageModule';
import { flushProductDetails, PHYSICAL_CARD_TYPE } from '../../productCartHandler/productCartModule';
import windowSize from '../../utils/windowSize';
import { getIsOldUiPreferred } from '../../app/bootstrap';

import NewFeatureBullet from '../NewFeatureBullet';
import { deviceType } from '../../utils/deviceTypeUtils';
import {
    responsiveCarouselV2
} from './CarouselConfig';

import ImageWithSkeleton from '../../common/ImageWithSkeleton';
import SiteSwitchDialog from '../../localeCurrencyPicker/SiteSwitchDialog';
import { getCartMaxNumPlasticCards } from '../../brand/brandSelectors';
import { getRemainingPlasticCardsAllowed } from '../../productCartHandler/productCartSelectors';
import { CARD_TYPES, INITIAL_FACEPLATES_COUNT } from '../../constants/constants';
import { setupCarouselArrowFocus, setupCarouselTabFocus } from '../../utils/carouselUtils';
import { setCurrency as setCurrencyActionCreator } from '../../intl/intlModule';
import { digitalHighlightBulletLists } from '../DigitalHighlightBulletLists';
import { physicalHighlightBulletLists } from '../PhysicalHighlightBulletLists';
import { printAtHomeHighlightBulletLists } from '../PrintAtHomeHighlightBulletLists';
import giftCardDetailsPageMessagesV2 from '../../giftCardDetailsPage_v2/messages/giftCardDetailsPageMessages';
import { HOME_STEP } from '../../routing/flowConstants';
import UYOButton from '../../common/UYOButton';
import { trackButtonClick } from '../../segment/segmentModule';
import { getFormattedMessage } from '../../utils/textUtils';
import homePageV2Messages from '../assets/messages/homePageMessages';

const FaceplateList = (props) => {
    const {
        category, faceplates, handleSubmit, saveSelectedCardAction, flushProductDetailsAction,
        giftCardDeliveryType, categoryCount, setFaceplatesCount,
        setCarouselLoaded, windowWidth, intl, cartMaxNumPlasticCards, remainingPlasticCardsAllowed,
        saveSelectedCardType, tempCurrency, setCurrency,
        setClickedOnUYOAction, shouldSetCurrency, cardType,
        renderCatalogAndDenom, digitalPrimaryFaceplate, physicalPrimaryFacePlate,
        saveSelectedCardOnBuyButtonClickAction, trackSegmentButtonClick, isPhotoSupported,
        isPhysicalCard,
        uyoFaceplate,
        hidePicker,
        checkoutSubHeaderText
    } = props;

    const [isDialogOpen, setDialogOpen] = React.useState(false);
    const [isMaxPhysicalItemModalOpen, setIsMaxPhysicalItemModalOpen] = useState(false);
    const [openNewFeatureDialog, setOpenNewFeatureDialog] = useState(false);
    const [selectedFaceplate, setSelectedFaceplate] = useState(null);
    const [showAllFaceplates, setShowAllFaceplates] = useState(false);

    const handleMaxLimitModalClose = value => setIsMaxPhysicalItemModalOpen(value);
    const handleMaxLimitModalOpen = () => setIsMaxPhysicalItemModalOpen(true);
    const safeCategory = category && category.replace(/[^a-zA-Z0-9]/g, '-');

    const handleCloseModel = value => setDialogOpen(value);

    const numberOfOptionsToShow = (faceplates.size > 1 || categoryCount > 1) ? 3 : 4;
    let sortedCardDeliveryTypeOptionsList = digitalHighlightBulletLists(intl);
    let newFeatureSummaryText = getFormattedMessage(
        intl, giftCardDetailsPageMessagesV2.newFeatureSummaryTextDigital
    );
    let newFeaturePopUpTitle = intl.formatMessage(
        giftCardDetailsPageMessagesV2.newFeaturePopUpTitleDigital
    );
    let newFeaturePopUpDescriptionText = intl.formatMessage(
        giftCardDetailsPageMessagesV2.newFeaturePopUpDescriptionDigital
    );
    let actionButtonLabel = intl.formatMessage(
        giftCardDetailsPageMessagesV2.singleFaceplateActionButtonDigital
    );
    let PageTitle = intl.formatMessage(
        giftCardDetailsPageMessagesV2.whyChooseDigitalLabel
    );
    if (cardType === CARD_TYPES.PrintAtHome) {
        sortedCardDeliveryTypeOptionsList = printAtHomeHighlightBulletLists(intl);
        actionButtonLabel = intl.formatMessage(
            giftCardDetailsPageMessagesV2.singleFaceplateActionButtonPrintAtHome
        );
        PageTitle = intl.formatMessage(
            giftCardDetailsPageMessagesV2.whyChoosePrintAtHomeLabel
        );
        newFeatureSummaryText = getFormattedMessage(
            intl, giftCardDetailsPageMessagesV2.newFeatureSummaryTextPrintAtHome
        );
        newFeaturePopUpTitle = intl.formatMessage(
            giftCardDetailsPageMessagesV2.newFeaturePopUpTitlePrintAtHome
        );
        newFeaturePopUpDescriptionText = intl.formatMessage(
            giftCardDetailsPageMessagesV2.newFeaturePopUpDescriptionPrintAtHome
        );
    } else if (cardType === CARD_TYPES.Physical) {
        sortedCardDeliveryTypeOptionsList = physicalHighlightBulletLists(intl);
        actionButtonLabel = intl.formatMessage(
            giftCardDetailsPageMessagesV2.singleFaceplateActionButtonPlastic
        );
        PageTitle = intl.formatMessage(
            giftCardDetailsPageMessagesV2.whyChoosePlasticLabel
        );
        newFeatureSummaryText = getFormattedMessage(
            intl, giftCardDetailsPageMessagesV2.newFeatureSummaryTextPlastic
        );
        newFeaturePopUpTitle = intl.formatMessage(
            giftCardDetailsPageMessagesV2.newFeaturePopUpTitlePlastic
        );
        newFeaturePopUpDescriptionText = intl.formatMessage(
            giftCardDetailsPageMessagesV2.newFeaturePopUpDescriptionPlastic
        );
    }

    const device = deviceType(windowWidth);

    const handleIconClick = () => {
        setOpenNewFeatureDialog(true);
        trackSegmentButtonClick(`${cardType} GC : Info button`, HOME_STEP, 'Homepage NEW callout info');
    };

    const handleCloseNewFeatureModel = () => {
        setOpenNewFeatureDialog(false);
    };

    const physicalCardMaxLimitDigitalButtonClick = () => {
        saveSelectedCardAction(category, remainingPlasticCardsAllowed === 0
            ? faceplates.get(0) : selectedFaceplate, true, giftCardDeliveryType === PHYSICAL_CARD_TYPE);
        handleSubmit(false);
    };

        const defaultFaceplate = cardType === CARD_TYPES.Physical
        ? physicalPrimaryFacePlate
        : digitalPrimaryFaceplate;
    const defaultCategory = safeCategory;

    
    const CTAButtonOnClick = (faceplate, submitButtonTitleName, isPhysicalCardType, isMaxPhysicalModelOpen = false) => {
        if (!isMaxPhysicalModelOpen) {
            setCurrency(tempCurrency);
        }
        
        saveSelectedCardOnBuyButtonClickAction(defaultCategory,
            faceplate,
            submitButtonTitleName,
            isPhysicalCardType);
        setClickedOnUYOAction(false);
        handleSubmit(isPhysicalCardType);
    };

    const faceplateOnClick = (faceplate) => {
        saveSelectedCardAction(category, faceplate, true, giftCardDeliveryType === PHYSICAL_CARD_TYPE);

        setSelectedFaceplate(faceplate);
        const isPhysicalCardType = giftCardDeliveryType === PHYSICAL_CARD_TYPE;
        saveSelectedCardType(cardType);
        if (isPhysicalCardType && remainingPlasticCardsAllowed === 0) {
            handleMaxLimitModalOpen();
            return;
        }

        if (getIsOldUiPreferred()) {
            flushProductDetailsAction();
        }
        if (shouldSetCurrency) {
            setCurrency(tempCurrency);
            setClickedOnUYOAction(false);
            handleSubmit(isPhysicalCardType);
        }
    };
    useEffect(() => {
        setFaceplatesCount(faceplates.size);
    }, [faceplates.size]);

    const [loadedImages, setLoadedImages] = useState(0);
    const totalImages = faceplates.size; 

    const handleImageLoad = () => {
        setLoadedImages(prev => prev + 1);
    };

    useEffect(() => {
        if (loadedImages === totalImages) {
            setCarouselLoaded(true);
            setLoadedImages(0);
        }
    }, [loadedImages, totalImages]);

    
    useEffect(() => {
        const cleanup = setupCarouselArrowFocus(300); 
        return cleanup;
    }, [category, giftCardDeliveryType]);

    
    const arrowKeyNavRef = useRef(false);
    useEffect(() => {
        setupCarouselTabFocus(arrowKeyNavRef);
    }, [category, giftCardDeliveryType]);

    useEffect(() => {
        function updateWidths() {
            const containerSelector = `.carousel-container-${safeCategory}.${giftCardDeliveryType === PHYSICAL_CARD_TYPE
                ? 'physical-carousel-container'
                : 'digital-carousel-container'
            }`;
            const container = document.querySelector(containerSelector);
            if (!container) return;
            const containerWidth = container.offsetWidth;
            let deviceConfig = device;
            if (device === 'tab') {
                deviceConfig = 'tablet';
            } else if (!responsiveCarouselV2[deviceConfig]) {
                deviceConfig = 'desktop'; 
            }

            const config = responsiveCarouselV2[deviceConfig];
            const { items } = config;
            const gutter = config.partialVisibilityGutter || 0;

            setTimeout(() => {
                const itemsList = container.querySelectorAll('.react-multi-carousel-item.image-item');
                if (itemsList && itemsList.length <= items) {
                    let dynamicWidth = (containerWidth - gutter) / items;
                    if (device === 'desktop') {
                        dynamicWidth -= 37;
                    }
                    itemsList.forEach((li) => {
                        li.style.width = `${dynamicWidth}px`;
                    });
                }
            }, 0);
        }

        updateWidths();
        window.addEventListener('resize', updateWidths);
        return () => window.removeEventListener('resize', updateWidths);
    }, [category, faceplates.size, device, windowWidth, giftCardDeliveryType]);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const visibleFaceplates = (isMobile && !showAllFaceplates)
        ? faceplates.slice(0, INITIAL_FACEPLATES_COUNT)
        : faceplates;
    const hasMoreFaceplates = isMobile && faceplates.size > INITIAL_FACEPLATES_COUNT;

    const renderCheckoutSubHeader = className => (
        <Grid item xs={12} className={className}>
            <Typography
                className='landing-page-description-text'
                variant='body2'
                component='p'
            >
                <Markdown>
                    {DOMPurify.sanitize(checkoutSubHeaderText)}
                </Markdown>
            </Typography>
        </Grid>
    );

    return (
        <>
            {(faceplates.size > 1 || categoryCount > 1) ? (
                <>
                    <Grid
                        container
                        spacing={{
                            xs: 0, sm: 1, md: 2, lg: 3
                        }}
                        columns={{
                            xs: 12, sm: 12, md: 12, lg: 12
                        }}
                        className='Grid-Container'
                    >
                        {visibleFaceplates.map(faceplate => (
                            <Grid
                                item
                                xs={6}
                                sm={4}
                                md={3}
                                lg={3}
                                key={faceplate.get('code')}
                                className='Grid-Item'
                            >
                                {}
                                <Link
                                    href="#"
                                    onClick={(e) => { e.preventDefault(); faceplateOnClick(faceplate); }}
                                    className={`faceplate-wrapper
                                        ${!shouldSetCurrency ? 'faceplate-wrapper-not-clickable' : ''}`}
                                    aria-disabled={!shouldSetCurrency}
                                    tabIndex={!shouldSetCurrency ? -1 : 0}
                                    sx={{
                                        ...((!shouldSetCurrency) && { pointerEvents: 'none', opacity: 0.5 })
                                    }}
                                >
                                    <ImageWithSkeleton
                                        src={faceplate.get('imageURL')}
                                        alt={faceplate.get('altText')}
                                        className='faceplates'
                                        onLoad={handleImageLoad}
                                        skeletonSx={{ aspectRatio: '5/3' }}
                                    />
                                </Link>
                            </Grid>
                        ))}
                    </Grid>
                    {isMobile && hasMoreFaceplates && (
                        <Box className='load-more-container'>
                            <Button
                                variant='outlined'
                                className='load-more-btn'
                                onClick={() => setShowAllFaceplates(prev => !prev)}
                            >
                                {showAllFaceplates ? intl.formatMessage(homePageV2Messages.showFewerDesigns) : intl.formatMessage(homePageV2Messages.loadMoreDesigns)}
                            </Button>
                        </Box>
                    )}
                </>
            ) : (
                <Box>
                    {checkoutSubHeaderText.trim() && renderCheckoutSubHeader(
                        isMobile ? 'mobile-checkout-sub-header-text' : 'checkout-sub-header-text'
                    )}
                    {isMobile && newFeatureSummaryText.trim() && (
                        <Grid
                            className='new-feature-bullet-grid'
                            item
                            xs={12}
                        >
                            <NewFeatureBullet
                                intl
                                newFeatureSummaryText={newFeatureSummaryText}
                                newFeaturePopUpTitle={newFeaturePopUpTitle}
                                newFeaturePopUpDescriptionText={newFeaturePopUpDescriptionText}
                                openNewFeatureDialog={openNewFeatureDialog}
                                handleIconClick={handleIconClick}
                                handleCloseNewFeatureModel={handleCloseNewFeatureModel}
                            />
                        </Grid>
                    )}
                    <Grid
                        className='single-faceplate-design-wrapper'
                        container
                        sx={{ display: 'flex', alignItems: 'center' }}
                    >
                        <Grid item xs={12} className='single-faceplate-design-container'>
                            {faceplates.map(faceplate => (
                                
                                <Link
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        faceplateOnClick(faceplate);
                                    }}
                                    key={faceplate.get('code')}
                                    className={`faceplate-wrapper
                                            ${!shouldSetCurrency ? 'faceplate-wrapper-not-clickable' : ''}`}
                                    aria-disabled={!shouldSetCurrency}
                                    tabIndex={!shouldSetCurrency ? -1 : 0}
                                    sx={{
                                        ...((!shouldSetCurrency) && { pointerEvents: 'none', opacity: 0.5 })
                                    }}
                                >
                                    <ImageWithSkeleton
                                        src={faceplate.get('imageURL')}
                                        alt={faceplate.get('altText')}
                                        className='faceplates single-faceplate'
                                        style={{ aspectRatio: '5/3' }}
                                    />
                                </Link>
                            ))}
                            {
                                isPhotoSupported && (
                                    <UYOButton
                                        onClick={() => {
                                            saveSelectedCardType(isPhysicalCard ? CARD_TYPES.Physical : CARD_TYPES.Digital);
                                            if (!uyoFaceplate) {
                                                saveSelectedCardOnBuyButtonClickAction(defaultCategory,
                                                    defaultFaceplate,
                                                    'UYO',
                                                    isPhysicalCard);
                                            }
                                            setClickedOnUYOAction(true); 
                                            handleSubmit(isPhysicalCard, true);
                                            trackSegmentButtonClick('Upload Your Own Photo', HOME_STEP, 'Homepage');
                                        }}
                                    />
                                )

                            }
                            {renderCatalogAndDenom()}
                            { !hidePicker && (
                                <Button
                                    variant='contained'
                                    className='submitButton'
                                    onClick={() => {
                                        saveSelectedCardType(cardType);
                                        if (cardType === CARD_TYPES.Physical && remainingPlasticCardsAllowed === 0) {
                                            handleMaxLimitModalOpen();
                                            return;
                                        }

                                        CTAButtonOnClick(defaultFaceplate, 'Button', cardType === CARD_TYPES.Physical);
                                    }}
                                >
                                    {actionButtonLabel}
                                </Button>
                            )

                            }
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            sx={{ display: { xs: 'none', sm: 'block' } }}
                            className='delivery-type-description-container'
                        >
                            <Typography className='delivery-type-description-title'>
                                {PageTitle}
                            </Typography>
                            {newFeatureSummaryText.trim()
                             && (
                                 <Grid item xs={12}>
                                     <NewFeatureBullet
                                         intl
                                         newFeatureSummaryText={newFeatureSummaryText}
                                         newFeaturePopUpTitle={newFeaturePopUpTitle}
                                         newFeaturePopUpDescriptionText={newFeaturePopUpDescriptionText}
                                         openNewFeatureDialog={openNewFeatureDialog}
                                         handleIconClick={handleIconClick}
                                         handleCloseNewFeatureModel={handleCloseNewFeatureModel}
                                     />
                                 </Grid>
                             )}
                            <ul className='eco-friendly-delivery-list'>
                                {sortedCardDeliveryTypeOptionsList.slice(
                                    0, numberOfOptionsToShow
                                ).map(item => (
                                    <li className='description-container' key={item.text}>
                                        <span className='eco-Friendly-icon'>
                                            {item.icon}
                                        </span>
                                        <Typography component="span" className='eco-Friendly-Label'>
                                            {item.text}
                                        </Typography>
                                    </li>
                                ))}
                            </ul>
                        </Grid>
                    </Grid>
                </Box>
            )}

            {isDialogOpen && (
                <SiteSwitchDialog
                    physicalCard
                    isOpen={isDialogOpen}
                    handleCloseModel={handleCloseModel}
                />
            )}
            {isMaxPhysicalItemModalOpen && (
                <SiteSwitchDialog
                    physicalCard
                    isOpen={isMaxPhysicalItemModalOpen}
                    handleCloseModel={handleMaxLimitModalClose}
                    isPhysicalcartMaxlimitReached={isMaxPhysicalItemModalOpen}
                    onConfirmCTA={() => physicalCardMaxLimitDigitalButtonClick()
                    }
                    cartMaxNumPlasticCards={cartMaxNumPlasticCards}
                />
            )}
        </>
    );
};

FaceplateList.defaultProps = {
    cartMaxNumPlasticCards: 0,
    cardType: '',
    renderCatalogAndDenom: () => null,
    hidePicker: false,
    checkoutSubHeaderText: ''
};

FaceplateList.propTypes = {
    category: PropTypes.string.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    saveSelectedCardAction: PropTypes.func.isRequired,
    flushProductDetailsAction: PropTypes.func.isRequired,
    faceplates: PropTypes.instanceOf(IList).isRequired,
    giftCardDeliveryType: PropTypes.string.isRequired,
    setFaceplatesCount: PropTypes.func.isRequired,
    setCarouselLoaded: PropTypes.func.isRequired,
    windowWidth: PropTypes.number.isRequired,
    cartMaxNumPlasticCards: PropTypes.number,
    remainingPlasticCardsAllowed: PropTypes.number.isRequired,
    saveSelectedCardType: PropTypes.func.isRequired,
    tempCurrency: PropTypes.string.isRequired,
    setCurrency: PropTypes.func.isRequired,
    setClickedOnUYOAction: PropTypes.func.isRequired,
    shouldSetCurrency: PropTypes.bool.isRequired,
    categoryCount: PropTypes.number.isRequired,
    intl: intlShape.isRequired,
    cardType: PropTypes.string,
    renderCatalogAndDenom: PropTypes.func,
    digitalPrimaryFaceplate: PropTypes.object.isRequired,
    physicalPrimaryFacePlate: PropTypes.object.isRequired,
    saveSelectedCardOnBuyButtonClickAction: PropTypes.func.isRequired,
    trackSegmentButtonClick: PropTypes.func.isRequired,
    isPhotoSupported: PropTypes.bool.isRequired,
    isPhysicalCard: PropTypes.bool.isRequired,
    uyoFaceplate: PropTypes.string.isRequired,
    hidePicker: PropTypes.bool,
    checkoutSubHeaderText: PropTypes.string
};

const mapStateToProps = (state, { category, giftCardDeliveryType }) => {
    const faceplateGroups = getFaceplateGroups(state);
    const faceplates = getCategorySpecificFaceplates(faceplateGroups, category,
        giftCardDeliveryType === PHYSICAL_CARD_TYPE);
    const cartMaxNumPlasticCards = getCartMaxNumPlasticCards(state);
    const remainingPlasticCardsAllowed = getRemainingPlasticCardsAllowed(state);
    const digitalPrimaryFaceplate = getDigitalPrimaryFaceplate(state);
    const physicalPrimaryFacePlate = getPhysicalPrimaryFaceplate(state);

    return {
        faceplates,
        cartMaxNumPlasticCards,
        remainingPlasticCardsAllowed,
        digitalPrimaryFaceplate,
        physicalPrimaryFacePlate
    };
};

const mapDispatchToProps = {
    saveSelectedCardAction: saveSelectedCard,
    flushProductDetailsAction: flushProductDetails,
    saveSelectedCardType: selectedCardType,
    setCurrency: setCurrencyActionCreator,
    setClickedOnUYOAction: setClickedOnUYO,
    saveSelectedCardOnBuyButtonClickAction: saveSelectedCardOnBuyButtonClick,
    trackSegmentButtonClick: trackButtonClick
};

export default connect(mapStateToProps, mapDispatchToProps)(windowSize(FaceplateList));
